export const USER_BADGE_IDS = {
  FOUNDING_PAGEHOPPER: 'founding-pagehopper',
  TOP_SELLER: 'top-seller',
  FAST_SHIPPER: 'fast-shipper',
  AVID_READER: 'avid-reader',
};

/**
 * Central registry for public PageHop user badges.
 *
 * Badges are assigned manually through profile.publicData.badges until qualification rules are
 * defined. eligibilityDescription intentionally documents intent without implementing thresholds.
 */
export const USER_BADGE_DEFINITIONS = [
  {
    id: USER_BADGE_IDS.FOUNDING_PAGEHOPPER,
    label: 'Founding PageHopper',
    labelMessageId: 'UserBadge.foundingPagehopperLabel',
    description: 'An early member of the PageHop Books community.',
    descriptionMessageId: 'UserBadge.foundingPagehopperDescription',
    icon: 'spark-book',
    variant: 'founding',
    eligibility: 'both',
    eligibilityDescription: 'Future founding-member cutoff rule; no automatic award yet.',
  },
  {
    id: USER_BADGE_IDS.TOP_SELLER,
    label: 'Top Seller',
    labelMessageId: 'UserBadge.topSellerLabel',
    description: 'Recognizes strong selling activity and performance.',
    descriptionMessageId: 'UserBadge.topSellerDescription',
    icon: 'award-star',
    variant: 'seller',
    eligibility: 'seller',
    eligibilityDescription: 'Future seller-performance rule; no automatic award yet.',
  },
  {
    id: USER_BADGE_IDS.FAST_SHIPPER,
    label: 'Fast Shipper',
    labelMessageId: 'UserBadge.fastShipperLabel',
    description: 'Recognizes consistently fast order shipment.',
    descriptionMessageId: 'UserBadge.fastShipperDescription',
    icon: 'fast-parcel',
    variant: 'shipping',
    eligibility: 'seller',
    eligibilityDescription: 'Future shipping-speed rule; no automatic award yet.',
  },
  {
    id: USER_BADGE_IDS.AVID_READER,
    label: 'Avid Reader',
    labelMessageId: 'UserBadge.avidReaderLabel',
    description: 'Recognizes frequent purchasing and reading activity.',
    descriptionMessageId: 'UserBadge.avidReaderDescription',
    icon: 'heart-book',
    variant: 'reader',
    eligibility: 'buyer',
    eligibilityDescription: 'Future buyer-activity rule; no automatic award yet.',
  },
];

const USER_BADGES_BY_ID = Object.fromEntries(
  USER_BADGE_DEFINITIONS.map(definition => [definition.id, definition])
);

/**
 * Resolve persisted badge IDs into known definitions while preserving registry order.
 * Unknown and duplicate IDs are ignored so arbitrary profile data cannot become badge copy.
 */
export const getUserBadgeDefinitions = badgeIds => {
  if (!Array.isArray(badgeIds)) {
    return [];
  }

  const persistedIds = new Set(badgeIds.filter(id => typeof id === 'string'));
  return USER_BADGE_DEFINITIONS.filter(definition => persistedIds.has(definition.id));
};

/** Read public badge IDs from a Sharetribe user entity. */
export const getUserBadgeIds = user => user?.attributes?.profile?.publicData?.badges || [];

/** Resolve the public badges assigned to a Sharetribe user entity. */
export const getUserBadges = user => getUserBadgeDefinitions(getUserBadgeIds(user));

/** Resolve one badge definition by its stable ID. */
export const getUserBadgeDefinition = badgeId => USER_BADGES_BY_ID[badgeId] || null;